var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
// Mixins
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
// Utils
import { getRandomId } from './Navigation.utils.js';
// Components
import { NavigationOverflowMenu } from './NavigationOverflowMenu.js';
import { Collapsible } from '@frontend_zz/collapsible/Collapsible.js';
import { Icon } from '@frontend_zz/icon/Icon.js';
import { Popover } from '@frontend_zz/popover/Popover.js';
import { Text } from '@frontend_zz/text/Text.js';
// Styling
import styles from './NavigationItem.css.js';
// Data
import content from './Navigation.content.json';
// Defs
const USE_COLLAPSIBLE = false;
/**
 * @element zz-navigation-item
 */
export class NavigationItem extends BaseElement {
    constructor() {
        super(...arguments);
        /**
         * The level of this nav item in the hierarchy
         */
        this.level = 1;
        /**
         * Whether or not this nav item is part of an overflow menu
         */
        this.isOverflow = false;
        /**
         * Whether or not this nav item is in 'active' state
         */
        this.isActive = false;
        /**
         * Whether or not this nav item is in 'expanded' state (for nav items that have sub items and a disclosure mechnanism)
         */
        this.isExpanded = false;
        /**
         * Whether or not this nav item is considered the primary overflow ("more"); there can only be one of these and it needs special treatment
         */
        this.isPrimaryOverflow = false;
        /**
         * Whether or not to only render the overflow this nav item generates and not the item itself; for use with hidden nav bars that are triggered externally (e.g. the public website)
         */
        this.onlyRenderOverflow = false;
        /**
         * Derived state variables
         */
        this.showIcon = false;
        this.effectiveIcon = 'chevronRight';
        this.hasSubItems = false;
        this.hasOverflowMenu = false;
        this.hasCollapsibleMenu = false;
        this.updated = () => {
            const { data, isExpanded, isMobile, isOverflow, level, isPrimaryOverflow } = this;
            const { href, icon, iconActive, isActive, type, subItems } = data;
            const hasSubItems = !!subItems.length;
            // Set computed/derived props
            this.disclosureId = `disclosure-${encodeURIComponent(href || getRandomId())}`;
            this.type = type;
            this.isActive = isActive;
            this.showIcon = (isMobile || isOverflow) && type != 'context-switch';
            this.hasSubItems = hasSubItems;
            this.hasOverflowMenu = isPrimaryOverflow || (!isMobile && hasSubItems && !isOverflow && level <= 1);
            this.hasCollapsibleMenu = USE_COLLAPSIBLE && isMobile && hasSubItems && isOverflow;
            this.effectiveIcon = (isActive || isExpanded) && iconActive ? iconActive : icon || 'chevronRight';
        };
        this.onClick = (event) => {
            const { data, hasSubItems, hasOverflowMenu, hasCollapsibleMenu, isOverflow } = this;
            const { id, label, href } = data;
            const nextIsExpanded = !this.isExpanded;
            if (hasOverflowMenu || hasCollapsibleMenu) {
                event.preventDefault();
                this.isExpanded = nextIsExpanded;
                this.dispatchEvent(new CustomEvent('toggle-expanded', {
                    detail: {
                        element: this,
                        isExpanded: nextIsExpanded,
                        id,
                        label,
                        href,
                        nativeEvent: event,
                    },
                }));
            }
            else if (hasSubItems && isOverflow) {
                event.preventDefault();
            }
            else {
                this.dispatchEvent(new CustomEvent('universalAnchorClick', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        id,
                        label,
                        href,
                        nativeEvent: event,
                    },
                }));
            }
        };
        this.renderOverflowMenu = (items, level = 1) => {
            const { isExpanded, hasOverflowMenu, disclosureId } = this;
            const nonContextSwitchItems = items.filter((item) => item.type !== 'context-switch');
            const contextSwitchItems = items.filter((item) => item.type === 'context-switch');
            const onDismiss = (_event) => {
                this.isExpanded = false;
            };
            const renderNavigationItem = (item) => {
                return html `
                <zz-navigation-item
                    class="nav-item"
                    .data=${item}
                    level=${level}
                    is-overflow
                    ?is-expanded=${true}
                ></zz-navigation-item>
            `;
            };
            const renderContextSwitchMenu = () => html `
            <div class="context-switch-menu">${repeat(contextSwitchItems, (item) => renderNavigationItem(item))}</div>
        `;
            return html `
            <zz-navigation-overflow-menu
                class="overflow-menu-wrapper"
                id=${ifDefined(hasOverflowMenu ? disclosureId : undefined)}
                ?is-open=${isExpanded}
                app-bar-title=${content.overflowAppBarTitle}
                @dismiss=${onDismiss}
            >
                <ul class="overflow-menu">
                    ${repeat(nonContextSwitchItems, (item) => renderNavigationItem(item))}
                    ${when(contextSwitchItems.length, renderContextSwitchMenu)}
                </ul>
            </zz-navigation-overflow-menu>
        `;
        };
        this.render = () => {
            const { disclosureId, data, isExpanded, hasSubItems, hasOverflowMenu, hasCollapsibleMenu, isOverflow, level, onlyRenderOverflow, showIcon, effectiveIcon, renderOverflowMenu, onClick, isMobile, } = this;
            const { type, label, href, isActive, subItems } = data;
            const renderSubItem = (data) => {
                return html `
                <zz-navigation-item
                    class="nav-item"
                    .data=${data}
                    level=${level + 1}
                    ?is-overflow=${isOverflow}
                ></zz-navigation-item>
            `;
            };
            const renderSubMenu = () => {
                const content = html `
                <ul
                    class="menu"
                    id=${ifDefined(hasCollapsibleMenu ? disclosureId : undefined)}
                    aria-hidden=${ifDefined(hasCollapsibleMenu ? !isExpanded : undefined)}
                >
                    ${repeat(subItems, renderSubItem)}
                </ul>
            `;
                if (USE_COLLAPSIBLE) {
                    return html `
                    ${when(isMobile, () => html `<zz-collapsible ?is-open=${isExpanded}>${content}</zz-collapsible>`, () => content)}
                `;
                }
                return content;
            };
            const renderDisclosureTriggerIcon = () => html `<zz-icon class="disclosure-trigger-icon" type="chevronDownMini"></zz-icon>`;
            const renderContextSwitchActiveIcon = () => html `<zz-icon class="context-switch-active-icon" type="check" size="s"></zz-icon>`;
            const renderLabel = () => {
                // We use the unsafeHTML due to lack of other options to support any custom styling for the labels ex. underline
                return unsafeHTML(label);
            };
            const renderLink = () => html `
            <a
                class="nav-link"
                href=${ifDefined(!hasSubItems || !isOverflow ? href : undefined)}
                aria-controls=${ifDefined(hasOverflowMenu || hasCollapsibleMenu ? disclosureId : undefined)}
                aria-expanded=${ifDefined(hasOverflowMenu || hasCollapsibleMenu ? isExpanded : undefined)}
                @click=${onClick}
            >
                ${when(showIcon, () => html `<zz-icon type=${effectiveIcon}></zz-icon>`)}
                <zz-text
                    class="nav-link-text"
                    size=${ifDefined(isMobile && !isOverflow ? 'xxs' : undefined)}
                    tag="span"
                    weight=${isActive ? 'bold' : 'normal'}
                >
                    ${renderLabel()} ${when(type === 'context-switch' && isActive, renderContextSwitchActiveIcon)}
                </zz-text>
                ${when((!isMobile && hasOverflowMenu) || (USE_COLLAPSIBLE && isMobile && hasSubItems && isOverflow), renderDisclosureTriggerIcon)}
            </a>
        `;
            const renderPopover = () => {
                return html `
                <zz-popover ?is-visible=${isExpanded} enable-arrow theme="white">
                    <span slot="trigger">${renderLink()}</span>
                    ${renderOverflowMenu(subItems, level + 1)}
                </zz-popover>
            `;
            };
            const shouldRenderPopover = hasOverflowMenu && !isMobile;
            const shouldRenderLink = !onlyRenderOverflow && (!hasOverflowMenu || isMobile);
            const shouldRenderOverflowMenu = hasOverflowMenu && isMobile;
            const shouldRenderSubMenu = hasSubItems && isOverflow;
            return html `
            <li class="nav-item">
                ${when(shouldRenderPopover, renderPopover)} ${when(shouldRenderLink, renderLink)}
                ${when(shouldRenderOverflowMenu, () => renderOverflowMenu(subItems, level + 1), () => when(shouldRenderSubMenu, renderSubMenu))}
            </li>
        `;
        };
    }
    static get styles() {
        return [styles];
    }
    static get scopedElements() {
        return {
            'zz-collapsible': Collapsible,
            'zz-icon': Icon,
            'zz-popover': Popover,
            'zz-text': Text,
            'zz-navigation-item': NavigationItem,
            'zz-navigation-overflow-menu': NavigationOverflowMenu,
        };
    }
}
__decorate([
    property({ type: Object })
], NavigationItem.prototype, "data", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'level' })
], NavigationItem.prototype, "level", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-overflow' })
], NavigationItem.prototype, "isOverflow", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-active' })
], NavigationItem.prototype, "isActive", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-expanded' })
], NavigationItem.prototype, "isExpanded", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'is-primary-overflow' })
], NavigationItem.prototype, "isPrimaryOverflow", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'only-render-overflow' })
], NavigationItem.prototype, "onlyRenderOverflow", void 0);
__decorate([
    property({ attribute: 'animation-direction' })
], NavigationItem.prototype, "animationDirection", void 0);
__decorate([
    property({ reflect: true })
], NavigationItem.prototype, "type", void 0);
__decorate([
    state()
], NavigationItem.prototype, "showIcon", void 0);
__decorate([
    state()
], NavigationItem.prototype, "effectiveIcon", void 0);
__decorate([
    state()
], NavigationItem.prototype, "hasSubItems", void 0);
__decorate([
    state()
], NavigationItem.prototype, "hasOverflowMenu", void 0);
__decorate([
    state()
], NavigationItem.prototype, "hasCollapsibleMenu", void 0);
